import type { IHttpClient } from '@wix/yoshi-flow-editor';
import {
  addToCurrentCart,
  createCart,
  currentCartGetCurrentCart,
  removeLineItemsFromCurrentCart,
  updateCurrentCart,
} from '@wix/ambassador-ecom-v1-cart/http';
import type {
  AddToCurrentCartRequest,
  GetCurrentCartRequest,
  RemoveLineItemsFromCurrentCartRequest,
  AddToCartResponse,
  GetCurrentCartResponse,
  RemoveLineItemsResponse,
  SelectedShippingOption,
  Address,
  UpdateCartRequest,
  UpdateCartResponse,
  CreateCartRequest,
  CreateCartResponse,
} from '@wix/ambassador-ecom-v1-cart/types';
import type { FedopsLogger } from '../utils/monitoring/FedopsLogger';
import { getMonitoredApiCall } from './utils/getMonitoredApiCall';
import { withMonitorRequestIdApiCall } from './utils/withMonitorRequestIdApiCall';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import type { PanoramaErrorMonitor } from '@wix/panorama-client/dist/types/panorama-error-monitor';
import type { ErrorMonitorUserData } from '@wix/panorama-client/dist/types/types';
import type { IBIReporterService } from 'root/services/biReporterService';
import { getOperationBIEventSettings } from 'root/utils/BIDurationUtils';
import { menusState } from 'root/states/MenusState';
import { itemState } from 'root/states/itemsState';

export interface ShippingRequest {
  shippingOption: SelectedShippingOption;
  address?: Address;
}

const MONITOR_TYPE = 'cart';

const CART_MONITOR_CATEGORY = {
  CREATE_CART: 'createCart',
  ADD_ITEM: 'addItemToCart',
  GET_CURRENT_CART: 'getCurrentCart',
  REMOVE_ITEM: 'removeItemFromCart',
  UPDATE_CART_INFO: 'updateCartInfo',
};

export interface ICartClient {
  createCart: (request: CreateCartRequest) => Promise<CreateCartResponse>;
  addItemToCart: (request: AddToCurrentCartRequest) => Promise<AddToCartResponse>;
  getCurrentCart: (request: GetCurrentCartRequest) => Promise<GetCurrentCartResponse>;
  removeItemsFromCart: (
    item: RemoveLineItemsFromCurrentCartRequest
  ) => Promise<RemoveLineItemsResponse>;
  updateCartInfo: (updateCartRequest: UpdateCartRequest) => Promise<UpdateCartResponse>;
}

export const CartClient = (
  httpClient: IHttpClient,
  fedopsLogger: FedopsLogger,
  errorMonitor: PanoramaErrorMonitor<ErrorMonitorUserData> | undefined,
  errorHandler: ErrorHandlerPublicAPI,
  biReporterService?: IBIReporterService
): ICartClient => ({
  createCart: async (request: CreateCartRequest) => {
    const { withErrorHandler, getResolvedError } = errorHandler;
    try {
      const { data = {} } = await getMonitoredApiCall({
        ...getOperationBIEventSettings(menusState, itemState, biReporterService),
        name: 'createCart',
        callback: () =>
          withErrorHandler<CreateCartResponse>(
            () =>
              fetchAndMonitorRequestId<CreateCartRequest, CreateCartResponse>({
                httpClient,
                ambassadorApi: createCart,
                request,
                errorMonitor,
                category: CART_MONITOR_CATEGORY.ADD_ITEM,
              }),
            { errorCodesMap: {} }
          ),
        fedops: {
          start: fedopsLogger.createCartCallStarted,
          end: fedopsLogger.createCartCallEnded,
        },
        errorMonitor,
        onError: (e) => {
          const error = getResolvedError(e);
          console.error('Failed creating cart', error);
        },
      });
      return data;
    } catch (e: unknown) {
      // already reported inside the getMonitoredApiCall function
      return {};
    }
  },
  addItemToCart: async (request: AddToCurrentCartRequest) => {
    const { withErrorHandler, getResolvedError } = errorHandler;
    try {
      const { data = {} } = await getMonitoredApiCall({
        ...getOperationBIEventSettings(menusState, itemState, biReporterService),
        name: 'addItemToCart',
        callback: () =>
          withErrorHandler(
            () =>
              fetchAndMonitorRequestId<AddToCurrentCartRequest, AddToCartResponse>({
                httpClient,
                ambassadorApi: addToCurrentCart,
                request,
                errorMonitor,
                category: CART_MONITOR_CATEGORY.ADD_ITEM,
              }),
            { errorCodesMap: {} }
          ),
        fedops: {
          start: fedopsLogger.addItemsToCartCallStarted,
          end: fedopsLogger.addItemsToCartCallEnded,
        },
        errorMonitor,
        onError: (e) => {
          const error = getResolvedError(e);
          console.error('Failed adding item to cart', error);
        },
      });
      return data;
    } catch (e: unknown) {
      // already reported inside the getMonitoredApiCall function
      return {};
    }
  },
  getCurrentCart: async (request: GetCurrentCartRequest) => {
    const { withErrorHandler, getResolvedError } = errorHandler;
    try {
      const { data = {} } = await getMonitoredApiCall({
        callback: () =>
          withErrorHandler(
            () =>
              fetchAndMonitorRequestId<GetCurrentCartRequest, GetCurrentCartResponse>({
                httpClient,
                ambassadorApi: currentCartGetCurrentCart,
                request,
                errorMonitor,
                category: CART_MONITOR_CATEGORY.GET_CURRENT_CART,
              }),
            {
              errorCodesMap: {
                applicationError: {
                  OWNED_CART_NOT_FOUND: () => ({
                    message: 'Cart not exists',
                  }),
                  APP_NOT_INSTALLED: () => ({
                    message: 'App not installed',
                  }),
                  NOT_FOUND: () => ({
                    message: 'App not found',
                  }),
                },
              },
            }
          ),
        fedops: {
          start: fedopsLogger.getCartItemsStarted,
          end: fedopsLogger.getCartItemsEnded,
        },
        errorMonitor,
        onError: (e) => {
          const error = getResolvedError(e);
          console.error('Cart not exists', error);
        },
        excludeErrors: ['Request failed with status code 404'],
      });
      return data;
    } catch (e: unknown) {
      // already reported inside the getMonitoredApiCall function
      return {};
    }
  },
  removeItemsFromCart: async (items: RemoveLineItemsFromCurrentCartRequest) => {
    const { withErrorHandler, getResolvedError } = errorHandler;
    try {
      const { data = {} } = await getMonitoredApiCall({
        ...getOperationBIEventSettings(menusState, itemState, biReporterService),
        name: 'removeItemsFromCart',
        callback: () =>
          withErrorHandler(
            () =>
              fetchAndMonitorRequestId<
                RemoveLineItemsFromCurrentCartRequest,
                RemoveLineItemsResponse
              >({
                httpClient,
                ambassadorApi: removeLineItemsFromCurrentCart,
                request: items,
                errorMonitor,
                category: CART_MONITOR_CATEGORY.REMOVE_ITEM,
              }),
            { errorCodesMap: {} }
          ),
        fedops: {
          start: fedopsLogger.removeItemFromCartCallStarted,
          end: fedopsLogger.removeItemFromCartCallEnded,
        },
        onError: (e) => {
          const error = getResolvedError(e);
          console.error('Failed removing item from cart', error);
        },
        errorMonitor,
      });
      return data;
    } catch (e: unknown) {
      // already reported inside the getMonitoredApiCall function
      return {};
    }
  },
  updateCartInfo: async (updateCartRequest: UpdateCartRequest) => {
    const { withErrorHandler, getResolvedError } = errorHandler;
    try {
      const { data = {} } = await getMonitoredApiCall({
        ...getOperationBIEventSettings(menusState, itemState, biReporterService),
        name: 'updateCartInfo',
        callback: () =>
          withErrorHandler(
            () =>
              fetchAndMonitorRequestId<UpdateCartRequest, UpdateCartResponse>({
                httpClient,
                ambassadorApi: updateCurrentCart,
                request: updateCartRequest,
                errorMonitor,
                category: CART_MONITOR_CATEGORY.UPDATE_CART_INFO,
              }),
            { errorCodesMap: {} }
          ),
        fedops: {
          start: fedopsLogger.updateCartStarted,
          end: fedopsLogger.updateCartEnded,
        },
        errorMonitor,
        onError: (e) => {
          const error = getResolvedError(e);
          console.error('Failed updating cart', error);
        },
      });

      return data;
    } catch (e: unknown) {
      // already reported inside the getMonitoredApiCall function
      return {};
    }
  },
});

const fetchAndMonitorRequestId = <T, R>({
  httpClient,
  ambassadorApi,
  request,
  errorMonitor,
  category,
}: {
  httpClient: IHttpClient;
  ambassadorApi: Function;
  request: T;
  errorMonitor?: PanoramaErrorMonitor<ErrorMonitorUserData>;
  category: string;
}): Promise<R> =>
  withMonitorRequestIdApiCall<R>({
    errorMonitor,
    responsePromise: httpClient.request(ambassadorApi(request)),
    type: MONITOR_TYPE,
    category,
  }).then((response) => response.data);
